import { useState, useRef, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, Home, Users, Shield, AppWindow, Grid3X3, User, LogOut, Globe, Moon, Sun, Tag } from 'lucide-react';
import { useAuthStore } from '../store/authStore';
import Logo from './Logo';
import { cn } from '@/lib/utils';
import { useTranslation } from 'react-i18next';
import { useTheme } from '@/hooks/useTheme';

export default function Layout({ children }: { children: React.ReactNode }) {
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [langOpen, setLangOpen] = useState(false);
  const langRef = useRef<HTMLDivElement>(null);
  const location = useLocation();
  const { user, logout } = useAuthStore();
  const { t, i18n } = useTranslation();
  const { isDark, toggleTheme } = useTheme();

  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (langRef.current && !langRef.current.contains(e.target as Node)) {
        setLangOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const navigation = [
    { slug: 'dashboard',   name: t('layout.nav.dashboard'),   href: '/',           icon: Home },
    { slug: 'users',       name: t('layout.nav.users'),       href: '/users',      icon: Users },
    { slug: 'groups',      name: t('layout.nav.groups'),      href: '/groups',     icon: Shield },
    { slug: 'apps',        name: t('layout.nav.apps'),        href: '/apps',       icon: AppWindow },
    { slug: 'attributes',  name: t('layout.nav.attributes'),  href: '/attributes', icon: Tag },
    { slug: 'matrix',      name: t('layout.nav.matrix'),      href: '/matrix',     icon: Grid3X3 },
    { slug: 'profile',     name: t('layout.nav.profile'),     href: '/profile',    icon: User },
  ];

  const isActive = (href: string) =>
    href === '/' ? location.pathname === '/' : location.pathname.startsWith(href);

  const currentLang = i18n.language.startsWith('en') ? 'en' : 'es';

  return (
    <div className="min-h-screen bg-background">
      {/* Sidebar */}
      <aside
        className={cn(
          'fixed inset-y-0 left-0 z-50 w-64 bg-sidebar text-sidebar-foreground border-r border-sidebar-foreground/10 shadow-lg transform transition-transform duration-300 ease-in-out flex flex-col',
          sidebarOpen ? 'translate-x-0' : '-translate-x-full'
        )}
      >
        {/* Logo */}
        <div className="flex items-center justify-between h-20 px-6 border-b border-sidebar-foreground/10">
          <Logo className="h-8" tone="white" />
          <button
            onClick={() => setSidebarOpen(false)}
            className="lg:hidden text-sidebar-foreground/50 hover:text-sidebar-foreground transition-colors"
          >
            <X size={18} />
          </button>
        </div>

        {/* Navigation */}
        <nav className="flex-1 px-3 py-5 space-y-1 overflow-y-auto">
          {navigation.map((item) => {
            const Icon = item.icon;
            return (
              <Link
                key={item.href}
                to={item.href}
                className={cn(
                  'flex items-center px-3 py-2.5 text-sm font-medium rounded-md transition-all animate-slide-in-left',
                  isActive(item.href)
                    ? 'bg-primary text-primary-foreground shadow-sm'
                    : 'text-sidebar-foreground/70 hover:bg-sidebar-foreground/10 hover:text-sidebar-foreground'
                )}
              >
                <Icon className="w-4 h-4 mr-3 shrink-0" />
                {item.name}
              </Link>
            );
          })}
        </nav>

        {/* User info & logout */}
        <div className="p-4 border-t border-sidebar-foreground/10">
          <div className="mb-3 px-3">
            <p className="text-sm font-semibold text-sidebar-foreground truncate">{user?.name ?? user?.email}</p>
            <p className="text-xs text-sidebar-foreground/50 truncate">{user?.email}</p>
          </div>
          <button
            onClick={logout}
            className="flex items-center w-full px-3 py-2 text-sm text-sidebar-foreground/60 hover:bg-sidebar-foreground/10 hover:text-sidebar-foreground rounded-md transition-colors"
          >
            <LogOut className="w-4 h-4 mr-3 shrink-0" />
            {t('layout.signOut')}
          </button>
        </div>
      </aside>

      {/* Main content */}
      <div className={cn('transition-all duration-300', sidebarOpen ? 'lg:ml-64' : 'ml-0')}>
        {/* Top bar */}
        <header className="sticky top-0 z-40 h-16 glass flex items-center px-6 gap-4">
          <button
            onClick={() => setSidebarOpen(!sidebarOpen)}
            className="text-muted-foreground hover:text-foreground transition-colors"
          >
            <Menu size={20} />
          </button>
          <h2 className="text-base font-semibold text-foreground">{t('layout.title')}</h2>

          <div className="ml-auto flex items-center gap-1">
            {/* Language switcher */}
            <div ref={langRef} className="relative">
              <button
                onClick={() => setLangOpen(!langOpen)}
                title={t('layout.language')}
                className="h-8 w-8 rounded-md flex items-center justify-center bg-sidebar text-sidebar-foreground border border-white/20 hover:bg-accent hover:border-accent transition-colors"
              >
                <Globe className="w-4 h-4" />
              </button>

              {langOpen && (
                <div className="absolute right-0 top-10 w-36 bg-card border border-border rounded-lg shadow-xl py-1 z-50 animate-scale-in">
                  <button
                    onClick={() => { i18n.changeLanguage('es'); setLangOpen(false); }}
                    className={cn(
                      'w-full flex items-center gap-2 px-3 py-2 text-sm transition-colors hover:bg-muted',
                      currentLang === 'es' ? 'text-primary font-semibold' : 'text-foreground'
                    )}
                  >
                    <span>🇦🇷</span> Español
                  </button>
                  <button
                    onClick={() => { i18n.changeLanguage('en'); setLangOpen(false); }}
                    className={cn(
                      'w-full flex items-center gap-2 px-3 py-2 text-sm transition-colors hover:bg-muted',
                      currentLang === 'en' ? 'text-primary font-semibold' : 'text-foreground'
                    )}
                  >
                    <span>🇺🇸</span> English
                  </button>
                </div>
              )}
            </div>

            {/* Dark mode toggle */}
            <button
              onClick={toggleTheme}
              title={isDark ? t('layout.theme.toLight') : t('layout.theme.toDark')}
              className="h-8 w-8 rounded-md flex items-center justify-center bg-sidebar text-sidebar-foreground border border-white/20 hover:bg-accent hover:border-accent transition-colors"
            >
              {isDark ? <Sun className="w-4 h-4" /> : <Moon className="w-4 h-4" />}
            </button>
          </div>
        </header>

        <main className="p-6 min-h-[calc(100vh-4rem)]">{children}</main>
      </div>

      {/* Mobile overlay */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 z-40 bg-black/50 lg:hidden backdrop-blur-sm"
          onClick={() => setSidebarOpen(false)}
        />
      )}
    </div>
  );
}
